import React, { useState } from 'react';

const BLOCKED_DOMAINS = new Set([
  'gmail.com','yahoo.com','hotmail.com','outlook.com','icloud.com',
  'aol.com','live.com','msn.com','me.com','yahoo.co.uk','hotmail.co.uk',
  'protonmail.com','proton.me','ymail.com','rocketmail.com','mail.com',
  'zoho.com','inbox.com','gmx.com','gmx.net','fastmail.com',
  'hey.com','tutanota.com','mailfence.com','yandex.com','yandex.ru',
]);

const BRAND = {
  navy:  '#0D2137',
  teal:  '#1B6B72',
  gold:  '#C9A84C',
  cream: '#F5F0E8',
};

const BULLETS = [
  'Monthly sector spotlight across six core industries',
  'Featured in Newsroom, Magazine, and Web Edition',
  'Automatic Partner Employer Status with 25% off Career Hub job postings',
];

const COVER_THUMB = 'https://ik.imagekit.io/enrollforge/downloads/organization-in-focus-cover.jpg';

const overlay = {
  position: 'fixed', inset: 0, zIndex: 9999,
  background: 'rgba(13,33,55,0.72)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '16px',
  fontFamily: "'Inter', 'Manrope', sans-serif",
};

const card = {
  background: '#fff',
  borderRadius: '12px',
  overflow: 'hidden',
  display: 'flex',
  width: '100%',
  maxWidth: '860px',
  maxHeight: '90vh',
  boxShadow: '0 24px 64px rgba(13,33,55,0.28)',
};

const teaser = {
  background: BRAND.teal,
  color: '#fff',
  padding: '40px 36px',
  width: '320px',
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
};

const formPanel = {
  flex: 1,
  padding: '40px 36px',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  border: `1px solid #d0d5db`,
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'inherit',
  color: BRAND.navy,
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  fontSize: '12px',
  fontWeight: '600',
  color: BRAND.navy,
  marginBottom: '4px',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

const submitBtn = {
  width: '100%',
  padding: '12px',
  background: BRAND.navy,
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  fontSize: '15px',
  fontWeight: '600',
  fontFamily: 'inherit',
  cursor: 'pointer',
  transition: 'background 0.2s',
  letterSpacing: '0.02em',
};

export default function OrganizationInFocusModal({ onClose }) {
  const [view, setView] = useState('teaser'); // 'teaser' | 'form' | 'success'
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', organization: '', title: '',
  });
  const [emailError, setEmailError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  const API = process.env.REACT_APP_BACKEND_URL;

  function handleEmailChange(e) {
    const val = e.target.value;
    setForm(f => ({ ...f, email: val }));
    const domain = val.split('@')[1]?.toLowerCase() || '';
    setEmailError(domain && BLOCKED_DOMAINS.has(domain)
      ? 'Please use a business email address'
      : '');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setServerError('');
    const domain = form.email.split('@')[1]?.toLowerCase() || '';
    if (BLOCKED_DOMAINS.has(domain)) {
      setEmailError('Please use a business email address');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/brochure-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, brochure_type: 'organization-in-focus' }),
      });
      const data = await res.json();
      if (!res.ok) {
        setServerError(data.detail || 'Something went wrong. Please try again.');
      } else {
        setView('success');
      }
    } catch {
      setServerError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  function field(key, label, type = 'text') {
    return (
      <div key={key}>
        <label htmlFor={`oif-${key}`} style={labelStyle}>{label}</label>
        <input
          id={`oif-${key}`}
          data-testid={`oif-${key}`}
          type={type}
          value={form[key]}
          onChange={key === 'email' ? handleEmailChange : e => setForm(f => ({ ...f, [key]: e.target.value }))}
          style={{ ...inputStyle, borderColor: key === 'email' && emailError ? '#c0392b' : '#d0d5db' }}
          required
          autoComplete="off"
        />
        {key === 'email' && emailError && (
          <p data-testid="oif-email-error" style={{ color: '#c0392b', fontSize: '12px', marginTop: '4px' }}>
            {emailError}
          </p>
        )}
      </div>
    );
  }

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()} data-testid="oif-modal-overlay">
      <div style={card} role="dialog" aria-modal="true" aria-label="Organization in Focus Program">

        {/* ── Teaser panel ── */}
        <div style={teaser}>
          <div>
            <p style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.12em', color: BRAND.gold, textTransform: 'uppercase', margin: 0 }}>
              Sector Spotlight
            </p>
            <h2 style={{ fontSize: '22px', fontWeight: '800', margin: '8px 0 0', lineHeight: 1.25, fontFamily: "'Manrope', sans-serif" }}>
              Organization in Focus
            </h2>
          </div>

          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {BULLETS.map((b, i) => (
              <li key={i} style={{ display: 'flex', gap: '10px', fontSize: '13px', lineHeight: 1.5, color: '#d6f0ee' }}>
                <span style={{ color: BRAND.gold, fontSize: '16px', flexShrink: 0, marginTop: '1px' }}>&#10003;</span>
                {b}
              </li>
            ))}
          </ul>

          <img
            src={COVER_THUMB}
            alt="Organization in Focus Program brochure cover"
            style={{ width: '100%', borderRadius: '6px', border: `2px solid ${BRAND.gold}`, objectFit: 'cover', maxHeight: '160px' }}
            onError={e => { e.currentTarget.style.display = 'none'; }}
          />

          {view === 'teaser' && (
            <button
              data-testid="oif-request-brochure-btn"
              onClick={() => setView('form')}
              style={{ ...submitBtn, background: BRAND.gold, color: BRAND.navy, marginTop: 'auto' }}
            >
              Request the Brochure
            </button>
          )}
        </div>

        {/* ── Right panel ── */}
        <div style={formPanel}>
          <button
            data-testid="oif-close-btn"
            onClick={onClose}
            aria-label="Close"
            style={{
              alignSelf: 'flex-end', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '20px', color: '#8a9aaa', lineHeight: 1, padding: '0 2px',
            }}
          >
            &#10005;
          </button>

          {view === 'teaser' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', justifyContent: 'center', flex: 1 }}>
              <h3 style={{ fontSize: '20px', fontWeight: '700', color: BRAND.navy, margin: 0, fontFamily: "'Manrope', sans-serif" }}>
                Organization in Focus Program
              </h3>
              <p style={{ fontSize: '14px', color: '#4a5e72', lineHeight: 1.65, margin: 0 }}>
                The Organization in Focus Program brings selected organizations into the sector conversation each month. If you would like to learn more or bring your organization into focus, request the brochure.
              </p>
            </div>
          )}

          {view === 'form' && (
            <form onSubmit={handleSubmit} data-testid="oif-brochure-form" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: BRAND.navy, margin: 0, fontFamily: "'Manrope', sans-serif" }}>
                Request the Brochure
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {field('first_name', 'First Name')}
                {field('last_name', 'Last Name')}
              </div>
              {field('email', 'Business Email', 'email')}
              {field('organization', 'Organization')}
              {field('title', 'Title')}
              {serverError && (
                <p data-testid="oif-server-error" style={{ color: '#c0392b', fontSize: '13px', margin: 0 }}>
                  {serverError}
                </p>
              )}
              <button
                data-testid="oif-submit-btn"
                type="submit"
                disabled={submitting || !!emailError}
                style={{ ...submitBtn, opacity: submitting || emailError ? 0.6 : 1 }}
              >
                {submitting ? 'Sending…' : 'Request Access'}
              </button>
            </form>
          )}

          {view === 'success' && (
            <div data-testid="oif-success-message" style={{ display: 'flex', flexDirection: 'column', gap: '16px', justifyContent: 'center', flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: '40px', color: BRAND.teal }}>&#10003;</div>
              <h3 style={{ fontSize: '20px', fontWeight: '700', color: BRAND.navy, margin: 0, fontFamily: "'Manrope', sans-serif" }}>
                Request Received
              </h3>
              <p style={{ fontSize: '14px', color: '#4a5e72', lineHeight: 1.65, margin: 0 }}>
                Check your business email for your access link. The link expires in 48 hours.
              </p>
              <button
                data-testid="oif-done-btn"
                onClick={onClose}
                style={{ ...submitBtn, width: 'auto', alignSelf: 'center', background: BRAND.navy }}
              >
                Done
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
